import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';

/**
 * TargetMarginsDisplay Component
 * Displays target margin thresholds for the selected service
 * Helps sales staff understand what margins they should aim for
 */
export default function TargetMarginsDisplay({ selectedService, servicesFullData }) {
  const [thresholds, setThresholds] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedService) {
      fetchThresholds();
    } else {
      setThresholds(null);
    }
  }, [selectedService]);

  const fetchThresholds = async () => {
    try {
      setLoading(true);
      const response = await api.get('/approval-thresholds');
      const config = response.data;
      
      // Find the selected service details
      const serviceData = servicesFullData.find(s => s.name === selectedService);
      const serviceId = serviceData?.id;
      
      // Get service-specific thresholds or use global defaults
      const serviceSpecific = config.service_specific?.[serviceId] || {};
      const globalConfig = config.global || {};
      
      // Build threshold display data
      const managerThresholds = {
        gpm: serviceSpecific.manager?.gpm_threshold || globalConfig.manager?.gpm_threshold || 55,
        revenue: globalConfig.manager?.revenue_threshold || 10000,
        netRevenue: globalConfig.manager?.net_revenue_threshold || 7000
      };
      
      const cfoThresholds = {
        gpm: serviceSpecific.ceo_cfo?.gpm_threshold || globalConfig.ceo_cfo?.gpm_threshold || 48,
        revenue: globalConfig.ceo_cfo?.revenue_threshold || 7000,
        netRevenue: globalConfig.ceo_cfo?.net_revenue_threshold || 5000
      };
      
      setThresholds({
        serviceName: selectedService,
        hasServiceSpecific: !!serviceSpecific.manager || !!serviceSpecific.ceo_cfo,
        standard: {
          gpm: managerThresholds.gpm,
          description: 'Deals meeting or exceeding this GPM require no special approval'
        },
        manager: {
          gpm: `${cfoThresholds.gpm}% - ${managerThresholds.gpm}%`,
          description: 'Requires Manager approval'
        },
        cfo: {
          gpm: `< ${cfoThresholds.gpm}%`,
          description: 'Requires CFO/CEO approval'
        }
      });
    } catch (error) {
      console.error('Error fetching approval thresholds:', error);
      // Set default thresholds if API fails
      setThresholds({
        serviceName: selectedService,
        hasServiceSpecific: false,
        standard: {
          gpm: 55,
          description: 'Deals meeting or exceeding this GPM require no special approval'
        },
        manager: {
          gpm: '48% - 55%',
          description: 'Requires Manager approval'
        },
        cfo: {
          gpm: '< 48%',
          description: 'Requires CFO/CEO approval'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (!selectedService || !thresholds) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-r-lg">
        <div className="animate-pulse">
          <div className="h-4 bg-indigo-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-indigo-100 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-5">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
          <Target className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-base font-bold text-indigo-900">
              Target Margins for {thresholds.serviceName}
            </h3>
            {thresholds.hasServiceSpecific && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                Service-Specific
              </span>
            )}
          </div>
          
          <div className="space-y-3">
            {/* Standard Deals */}
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-slate-700">Standard (No Approval)</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-700">
                    ≥ {thresholds.standard.gpm}%
                  </div>
                  <div className="text-xs text-slate-500">GPM Target</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2">{thresholds.standard.description}</p>
            </div>

            {/* Manager Approval */}
            <div className="bg-white rounded-lg p-3 border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-slate-700">Manager Approval</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-amber-700">
                    {thresholds.manager.gpm}
                  </div>
                  <div className="text-xs text-slate-500">GPM Range</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2">{thresholds.manager.description}</p>
            </div>

            {/* CFO/CEO Approval */}
            <div className="bg-white rounded-lg p-3 border border-red-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-semibold text-slate-700">CFO/CEO Approval</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-red-700">
                    {thresholds.cfo.gpm}
                  </div>
                  <div className="text-xs text-slate-500">GPM Threshold</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2">{thresholds.cfo.description}</p>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-indigo-200">
            <p className="text-xs text-indigo-700 font-medium">
              💡 Aim for ≥{thresholds.standard.gpm}% GPM to avoid approval delays. Use the Calculator to optimize your pricing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
